import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { LotusLogo } from '../components/Layout';
import { useJazyk } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GoogleCallback() {
  const { t } = useJazyk();
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authLogin } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate('/auth', { replace: true });
      return;
    }

    const sessionId = match[1];
    // Check if this came from register or login flow
    const mode = localStorage.getItem('bloom_google_mode') || 'login';
    localStorage.removeItem('bloom_google_mode');

    axios.post(`${API}/auth/google/session`, { session_id: sessionId, mode })
      .then(r => {
        authLogin(r.data.token, r.data.user);
        navigate('/', { replace: true });
      })
      .catch(err => {
        const detail = err.response?.data?.detail;
        if (detail === 'no_account') {
          // User tried to login but doesn't have an account — redirect to register
          setError('no_account');
        } else {
          navigate('/auth', { replace: true });
        }
      });
  }, [navigate, authLogin]);

  if (error === 'no_account') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bloom-bg px-4" data-testid="google-no-account">
        <div className="max-w-sm w-full text-center space-y-4">
          <LotusLogo size={48} />
          <h2 className="font-serif text-xl font-bold text-bloom-text">{t('oauth.ucetNenalezen')}</h2>
          <p className="text-sm text-bloom-sub">
            {t('oauth.neexistujeGoogle')}
          </p>
          <div className="space-y-2">
            <button
              onClick={() => {
                localStorage.setItem('bloom_google_mode', 'register');
                const redirectUrl = window.location.origin + '/auth/google-callback';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-bloom-violet text-white rounded-lg hover:bg-bloom-violet/90 transition-colors"
              data-testid="google-register-redirect-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#fff"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fff"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff"/></svg>
              {t('oauth.registrovatGoogle')}
            </button>
            <button
              onClick={() => navigate('/auth', { replace: true })}
              className="w-full px-4 py-2.5 border border-border rounded-lg text-sm text-bloom-sub hover:bg-muted/50 transition-colors"
              data-testid="back-to-auth-btn"
            >
              {t('over.zpetNaPrihlaseni')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bloom-bg" data-testid="google-callback">
      <div className="text-center">
        <LotusLogo size={48} />
        <p className="mt-4 text-bloom-sub">{t('oauth.prihlasujiGoogle')}</p>
        <div className="spinner mt-3" />
      </div>
    </div>
  );
}
